import { useState, useCallback, useMemo, useRef } from 'react';
import { useAppActions, Rack, Shelf, Table, CheckoutZoneData, Area } from '../../app/appContext';
import type { PolygonEntityType } from '../../app/appContext';
import { patchArea, deleteArea } from '../../api/areas';
import { deleteShelf } from '../../api/shelves';
import { deleteRack } from '../../api/racks';
import { deleteTable } from '../../api/tables';
import { deleteCheckoutZone, deletePolygonGroup } from '../../api/checkoutControl';
import { loadPolygonData } from '../../utils/polygonLoader';
import { CHECKOUT_ENTITY_TYPES, isFourPointType } from '../../utils/polygonTypes';
import { clampOvershoot } from '../../utils/canvasGutter';
import { followRack, pointsDiffer } from '../../utils/shelfFollow';

export type EditingType = PolygonEntityType;

type EditingPolygon = {
  id: string;
  type: EditingType;
  points: [number, number][];
};

type SelectedPoint = {
  polygonId: string;
  type: EditingType;
  idx: number;
};

// Point-to-segment distance helper
function pointToSegmentDistance(
  px: number, py: number,
  ax: number, ay: number,
  bx: number, by: number
): { distance: number; projection: [number, number] } {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    const d = Math.sqrt((px - ax) ** 2 + (py - ay) ** 2);
    return { distance: d, projection: [ax, ay] };
  }
  let t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const projX = ax + t * dx;
  const projY = ay + t * dy;
  const d = Math.sqrt((px - projX) ** 2 + (py - projY) ** 2);
  return { distance: d, projection: [projX, projY] };
}

interface UsePolygonEditorOptions {
  onError?: (message: string) => void;
}

interface UsePolygonEditorReturn {
  editingPolygons: EditingPolygon[];
  activeEditingPolygonId: string | null;
  activeEditingType: EditingType | null;
  selectedPoints: SelectedPoint[];
  saving: boolean;
  isDirty: boolean;
  startEditing: (polygonId: string, type: EditingType, points: [number, number][], extend: boolean) => void;
  handlePointSelect: (polygonId: string, type: EditingType, idx: number, extend: boolean) => void;
  handleSelectPointsInRect: (rect: { x1: number; y1: number; x2: number; y2: number }, extend: boolean) => void;
  clearPointSelection: () => void;
  handlePointDragStart: (polygonId: string, type: EditingType, idx: number, pos: { x: number; y: number }) => void;
  handlePointDrag: (polygonId: string, idx: number, pos: { x: number; y: number }) => void;
  handlePolygonDragStart: (polygonId: string, type: EditingType, pos: { x: number; y: number }) => void;
  handlePolygonDragMove: (polygonId: string, type: EditingType, pos: { x: number; y: number }) => void;
  handlePolygonDrag: (pos: { x: number; y: number }) => void;
  handlePointDragEnd: () => void;
  handleSaveEdit: (filteredRacks: Rack[], filteredShelves: Shelf[], filteredTables: Table[], filteredCheckoutZones: CheckoutZoneData[], selectedCameraId: string | null, areasById?: Record<string, Area>) => Promise<void>;
  handleDeletePolygon: (racks: Rack[], shelves: Shelf[], tables: Table[], checkoutZones: CheckoutZoneData[], selectedCameraId: string | null) => Promise<void>;
  cancelEditing: () => void;
  handleEdgeClick: (normalizedPos: { x: number; y: number }) => boolean;
  insertPointAtEdge: (polygonId: string, type: EditingType, edgeIndex: number, normalizedPos: [number, number]) => void;
  deleteSelectedPoints: () => void;
  undo: () => void;
  canUndo: boolean;
}

export const usePolygonEditor = (options?: UsePolygonEditorOptions): UsePolygonEditorReturn => {
  const onError = options?.onError;
  const [editingPolygons, setEditingPolygons] = useState<EditingPolygon[]>([]);
  const [activeEditingPolygonId, setActiveEditingPolygonId] = useState<string | null>(null);
  const [activeEditingType, setActiveEditingType] = useState<EditingType | null>(null);
  const [selectedPoints, setSelectedPoints] = useState<SelectedPoint[]>([]);
  const [saving, setSaving] = useState(false);
  const actions = useAppActions();
  const dragStateRef = useRef<{
    startPointsByPolygon: Record<string, [number, number][]>;
    startPos: { x: number; y: number };
    selected: SelectedPoint[];
    selectedByPolygon: Record<string, number[]>;
  } | null>(null);
  // Snapshot of points when editing started — used to detect unsaved changes.
  const originalPointsRef = useRef<Map<string, [number, number][]>>(new Map());

  // Undo stack (max 50 snapshots)
  const undoStackRef = useRef<EditingPolygon[][]>([]);
  const MAX_UNDO = 50;

  const pushUndoSnapshot = useCallback(() => {
    // Deep-copy current editing polygons
    const snapshot = editingPolygons.map(p => ({
      ...p,
      points: p.points.map(pt => [...pt] as [number, number]),
    }));
    undoStackRef.current = [...undoStackRef.current.slice(-(MAX_UNDO - 1)), snapshot];
  }, [editingPolygons]);

  // Вершину можно вынести за кадр в пределах поля холста (canvasGutter).
  const clamp01 = clampOvershoot;

  const setSelection = useCallback((polygonId: string | null, type: PolygonEntityType | null) => {
    actions.setSelectedPolygon(polygonId, type);
  }, [actions]);

  const resetEditingState = useCallback(() => {
    setEditingPolygons([]);
    setActiveEditingPolygonId(null);
    setActiveEditingType(null);
    setSelectedPoints([]);
    originalPointsRef.current = new Map();
    undoStackRef.current = [];
    setSelection(null, null);
  }, [setSelection]);

  const startEditing = useCallback((
    polygonId: string,
    type: EditingType,
    points: [number, number][],
    extend: boolean
  ) => {
    setActiveEditingPolygonId(polygonId);
    setActiveEditingType(type);
    setSelection(polygonId, type);

    const key = `${type}:${polygonId}`;
    const snapshot = points.map(p => [...p] as [number, number]);

    setEditingPolygons(prev => {
      const nextPoints = points.map(p => [...p] as [number, number]);
      if (extend) {
        const exists = prev.some(p => `${p.type}:${p.id}` === key);
        if (exists) return prev;
        if (!originalPointsRef.current.has(key)) {
          originalPointsRef.current.set(key, snapshot);
        }
        return [...prev, { id: polygonId, type, points: nextPoints }];
      }
      originalPointsRef.current = new Map([[key, snapshot]]);
      undoStackRef.current = [];
      return [{ id: polygonId, type, points: nextPoints }];
    });

    if (!extend) {
      setSelectedPoints([]);
    }
  }, [setSelection]);

  const handlePointSelect = useCallback((
    polygonId: string,
    type: EditingType,
    idx: number,
    extend: boolean
  ) => {
    setSelectedPoints(prev => {
      const key = `${type}:${polygonId}:${idx}`;
      const exists = prev.some(p => `${p.type}:${p.polygonId}:${p.idx}` === key);
      if (extend) {
        if (exists) {
          return prev.filter(p => `${p.type}:${p.polygonId}:${p.idx}` !== key);
        }
        return [...prev, { polygonId, type, idx }];
      }
      // Non-extend: always reduce to just this point. Group-drag behavior is
      // preserved because dragStateRef is captured at dragStart from the
      // closure's selectedPoints (still the group at that moment).
      return [{ polygonId, type, idx }];
    });
  }, []);

  const handleSelectPointsInRect = useCallback((
    rect: { x1: number; y1: number; x2: number; y2: number },
    extend: boolean
  ) => {
    const minX = Math.min(rect.x1, rect.x2);
    const maxX = Math.max(rect.x1, rect.x2);
    const minY = Math.min(rect.y1, rect.y2);
    const maxY = Math.max(rect.y1, rect.y2);
    const selected: SelectedPoint[] = [];

    for (const poly of editingPolygons) {
      poly.points.forEach(([x, y], idx) => {
        if (x >= minX && x <= maxX && y >= minY && y <= maxY) {
          selected.push({ polygonId: poly.id, type: poly.type, idx });
        }
      });
    }

    setSelectedPoints(prev => {
      if (!extend) return selected;
      const selectedSet = new Set(selected.map(p => `${p.type}:${p.polygonId}:${p.idx}`));
      const prevSet = new Set(prev.map(p => `${p.type}:${p.polygonId}:${p.idx}`));
      const next: SelectedPoint[] = [];

      for (const p of prev) {
        const key = `${p.type}:${p.polygonId}:${p.idx}`;
        if (!selectedSet.has(key)) {
          next.push(p);
        }
      }

      for (const p of selected) {
        const key = `${p.type}:${p.polygonId}:${p.idx}`;
        if (!prevSet.has(key)) {
          next.push(p);
        }
      }

      return next;
    });
  }, [editingPolygons]);

  const clearPointSelection = useCallback(() => {
    setSelectedPoints([]);
  }, []);

  const handlePointDragStart = useCallback((
    polygonId: string,
    type: EditingType,
    idx: number,
    pos: { x: number; y: number }
  ) => {
    pushUndoSnapshot();
    const isSelected = selectedPoints.some(p => p.polygonId === polygonId && p.type === type && p.idx === idx);
    const selected = isSelected ? selectedPoints : [{ polygonId, type, idx }];
    if (!isSelected) {
      setSelectedPoints([{ polygonId, type, idx }]);
    }

    const startPointsByPolygon: Record<string, [number, number][]> = {};
    for (const poly of editingPolygons) {
      const key = `${poly.type}:${poly.id}`;
      startPointsByPolygon[key] = poly.points.map(p => [...p] as [number, number]);
    }

    const selectedByPolygon: Record<string, number[]> = {};
    for (const p of selected) {
      const key = `${p.type}:${p.polygonId}`;
      if (!selectedByPolygon[key]) selectedByPolygon[key] = [];
      selectedByPolygon[key].push(p.idx);
    }

    dragStateRef.current = {
      startPointsByPolygon,
      startPos: { x: pos.x, y: pos.y },
      selected,
      selectedByPolygon
    };
  }, [editingPolygons, selectedPoints]);

  const handlePolygonDragStart = useCallback((polygonId: string, type: EditingType, pos: { x: number; y: number }) => {
    pushUndoSnapshot();
    const grabbed = editingPolygons.find(p => p.id === polygonId && p.type === type);
    if (!grabbed) return;

    // Перетаскивание двигает ВСЁ текущее выделение разом: cmd/ctrl/shift-клик по
    // полигонам складывает их в editingPolygons, и потянув любой — едут все.
    // Для одного полигона это просто он сам (прежнее поведение).
    const startPointsByPolygon: Record<string, [number, number][]> = {};
    const selected: SelectedPoint[] = [];
    const selectedByPolygon: Record<string, number[]> = {};
    for (const p of editingPolygons) {
      const key = `${p.type}:${p.id}`;
      startPointsByPolygon[key] = p.points.map(pt => [...pt] as [number, number]);
      selectedByPolygon[key] = p.points.map((_pt, idx) => idx);
      p.points.forEach((_pt, idx) => selected.push({ polygonId: p.id, type: p.type, idx }));
    }
    setSelectedPoints(selected);

    dragStateRef.current = {
      startPointsByPolygon,
      startPos: { x: pos.x, y: pos.y },
      selected,
      selectedByPolygon,
    };
  }, [editingPolygons]);

  // Живое перетаскивание группы: сам схваченный полигон двигает Konva (смещение
  // его узла), а ОСТАЛЬНЫЕ выделенные полигоны мы сдвигаем в состоянии на ту же
  // дельту, чтобы вся группа ехала за курсором. Для одиночного выделения —
  // ранний выход: сохраняем прежний путь без перерисовки React на каждый кадр.
  const handlePolygonDragMove = useCallback((polygonId: string, type: EditingType, pos: { x: number; y: number }) => {
    const dragState = dragStateRef.current;
    if (!dragState) return;
    if (Object.keys(dragState.selectedByPolygon).length <= 1) return;
    const grabbedKey = `${type}:${polygonId}`;
    const dx = pos.x - dragState.startPos.x;
    const dy = pos.y - dragState.startPos.y;
    setEditingPolygons(prev => prev.map(poly => {
      const key = `${poly.type}:${poly.id}`;
      if (key === grabbedKey) return poly; // схваченный двигает Konva — его точки не трогаем
      const selectedForPoly = dragState.selectedByPolygon[key];
      if (!selectedForPoly || selectedForPoly.length === 0) return poly;
      const startPoints = dragState.startPointsByPolygon[key] || poly.points;
      const nextPoints: [number, number][] = startPoints.map((pt, i) => {
        if (!selectedForPoly.includes(i)) return pt;
        return [clamp01(pt[0] + dx), clamp01(pt[1] + dy)] as [number, number];
      });
      return { ...poly, points: nextPoints };
    }));
  }, []);

  const handlePointDrag = useCallback((polygonId: string, idx: number, pos: { x: number; y: number }) => {
    const dragState = dragStateRef.current;
    if (dragState) {
      const dx = pos.x - dragState.startPos.x;
      const dy = pos.y - dragState.startPos.y;
      setEditingPolygons(prev => prev.map(poly => {
        const key = `${poly.type}:${poly.id}`;
        const selectedForPoly = dragState.selectedByPolygon[key];
        if (!selectedForPoly || selectedForPoly.length === 0) return poly;
        const startPoints = dragState.startPointsByPolygon[key] || poly.points;
        const nextPoints: [number, number][] = startPoints.map((pt, i) => {
          if (!selectedForPoly.includes(i)) return pt;
          return [clamp01(pt[0] + dx), clamp01(pt[1] + dy)] as [number, number];
        });
        return { ...poly, points: nextPoints };
      }));
      return;
    }

    setEditingPolygons(prev => prev.map(poly => {
      if (poly.id !== polygonId) return poly;
      const nextPoints: [number, number][] = poly.points.map((pt, i) => (
        i === idx ? [clamp01(pos.x), clamp01(pos.y)] as [number, number] : pt
      ));
      return { ...poly, points: nextPoints };
    }));
  }, []);

  const handlePolygonDrag = useCallback((pos: { x: number; y: number }) => {
    const dragState = dragStateRef.current;
    if (!dragState) return;
    const dx = pos.x - dragState.startPos.x;
    const dy = pos.y - dragState.startPos.y;
    setEditingPolygons(prev => prev.map(poly => {
      const key = `${poly.type}:${poly.id}`;
      const selectedForPoly = dragState.selectedByPolygon[key];
      if (!selectedForPoly || selectedForPoly.length === 0) return poly;
      const startPoints = dragState.startPointsByPolygon[key] || poly.points;
      const nextPoints: [number, number][] = startPoints.map((pt, i) => {
        if (!selectedForPoly.includes(i)) return pt;
        return [clamp01(pt[0] + dx), clamp01(pt[1] + dy)] as [number, number];
      });
      return { ...poly, points: nextPoints };
    }));
  }, []);

  const handlePointDragEnd = useCallback(() => {
    dragStateRef.current = null;
  }, []);

  const handleSaveEdit = useCallback(async (
    filteredRacks: Rack[],
    filteredShelves: Shelf[],
    filteredTables: Table[],
    filteredCheckoutZones: CheckoutZoneData[],
    selectedCameraId: string | null,
    areasById?: Record<string, Area>,
  ) => {
    if (editingPolygons.length === 0 || !selectedCameraId) return;

    setSaving(true);
    try {
      // Стеллаж с переключателем «одна полка = стеллаж» (#1549): полка едет за стеллажом той же
      // трансформацией, если её саму сейчас не правят (Алексей 14.10, #1547 «single-shelf rack»).
      const editingShelfIds = new Set(editingPolygons.filter(p => p.type === 'shelf').map(p => String(p.id)));
      const followers: Promise<unknown>[] = [];
      for (const poly of editingPolygons) {
        if (poly.type !== 'rack' || poly.points.length < 3 || !areasById) continue;
        const rack = filteredRacks.find(r => String(r.id) === String(poly.id));
        if (!rack || !rack.single_shelf) continue;
        const rackShelves = filteredShelves.filter(sh => String(sh.rack) === String(rack.id));
        if (rackShelves.length !== 1 || editingShelfIds.has(String(rackShelves[0].id))) continue;
        const oldRack = areasById[String(rack.area)]?.points;
        const shelfArea = areasById[String(rackShelves[0].area)];
        if (!oldRack || !shelfArea || oldRack.length < 3 || !pointsDiffer(oldRack, poly.points)) continue;
        const next = followRack(oldRack, poly.points, shelfArea.points);
        followers.push(patchArea(String(rackShelves[0].area), { direction_vectors: next }));
      }

      const patches = editingPolygons.map(async (poly) => {
        if (poly.points.length < 3) return;
        let areaId = poly.id;
        if (poly.type === 'rack') {
          const rack = filteredRacks.find(r => r.id === poly.id);
          if (rack) areaId = rack.area;
        } else if (poly.type === 'shelf') {
          const shelf = filteredShelves.find(s => s.id === poly.id);
          if (shelf) areaId = shelf.area;
        } else if (poly.type === 'table') {
          const table = filteredTables.find(t => t.id === poly.id);
          if (table) areaId = table.area;
        } else if (CHECKOUT_ENTITY_TYPES.includes(poly.type)) {
          const cz = filteredCheckoutZones.find(z => z.id === poly.id);
          if (cz && cz.areas && cz.areas.length > 0) areaId = cz.areas[0];
        }
        await patchArea(areaId, {
          direction_vectors: poly.points
        });
      });

      await Promise.all([...patches, ...followers]);

      // Reload polygons
      await loadPolygonData(selectedCameraId, actions);
      resetEditingState();
    } catch (err) {
      console.error('Failed to save polygon:', err);
      onError?.('Не удалось сохранить полигон');
    } finally {
      setSaving(false);
    }
  }, [editingPolygons, actions, resetEditingState, onError]);

  const handleDeletePolygon = useCallback(async (
    racks: Rack[],
    shelves: Shelf[],
    tables: Table[],
    checkoutZones: CheckoutZoneData[],
    selectedCameraId: string | null
  ) => {
    if (editingPolygons.length !== 1 || !activeEditingPolygonId || !activeEditingType || !selectedCameraId) return;

    setSaving(true);
    try {
      if (activeEditingType === 'standalone') {
        await deleteArea(activeEditingPolygonId);
      } else if (activeEditingType === 'shelf') {
        const shelf = shelves.find(s => s.id === activeEditingPolygonId);
        if (shelf) {
          await deleteShelf(shelf.id);
          await deleteArea(shelf.area);
        }
      } else if (activeEditingType === 'rack') {
        const rack = racks.find(r => r.id === activeEditingPolygonId);
        if (rack) {
          await deleteRack(rack.id);
          const shelvesForRack = shelves.filter(s => s.rack === rack.id);
          for (const shelf of shelvesForRack) {
            await deleteArea(shelf.area);
          }
          await deleteArea(rack.area);
        }
      } else if (activeEditingType === 'table') {
        const table = tables.find(t => t.id === activeEditingPolygonId);
        if (table) {
          await deleteTable(table.id);
          await deleteArea(table.area);
        }
      } else if (CHECKOUT_ENTITY_TYPES.includes(activeEditingType)) {
        const cz = checkoutZones.find(z => z.id === activeEditingPolygonId);
        if (cz) {
          // Delete checkout zone (cascade deletes queue/staff), then group & areas
          await deleteCheckoutZone(cz.id);
          if (cz.group) {
            // Delete areas in group, then the group
            for (const areaId of cz.areas) {
              try { await deleteArea(areaId); } catch { /* area may already be gone */ }
            }
            try { await deletePolygonGroup(cz.group); } catch { /* group may already be gone */ }
          }
        }
      }

      await loadPolygonData(selectedCameraId, actions);
      resetEditingState();
    } catch (err) {
      console.error('Failed to delete polygon:', err);
      onError?.('Не удалось удалить полигон');
    } finally {
      setSaving(false);
    }
  }, [editingPolygons.length, activeEditingPolygonId, activeEditingType, actions, resetEditingState, onError]);

  const cancelEditing = useCallback(() => {
    resetEditingState();
  }, [resetEditingState]);

  // Edge click: insert a point on the nearest edge if click is close enough.
  // Threshold is generous so clicking *on* the visible edge stroke reliably
  // counts (~25 px on a 1000-px canvas).
  const handleEdgeClick = useCallback((normalizedPos: { x: number; y: number }): boolean => {
    const THRESHOLD = 0.025; // normalized distance threshold
    let bestMatch: {
      polyIndex: number;
      edgeIndex: number;
      projection: [number, number];
      distance: number;
    } | null = null;

    for (let pi = 0; pi < editingPolygons.length; pi++) {
      const poly = editingPolygons[pi];
      const pts = poly.points;
      // Some entity types must stay rectangles because downstream projection
      // logic assumes exactly 4 vertices (racks, shelves, tables — see
      // FOUR_POINT_TYPES). Other types can take arbitrary vertex counts.
      if (isFourPointType(poly.type)) continue;

      for (let i = 0; i < pts.length; i++) {
        const a = pts[i];
        const b = pts[(i + 1) % pts.length];
        const { distance, projection } = pointToSegmentDistance(
          normalizedPos.x, normalizedPos.y,
          a[0], a[1], b[0], b[1]
        );
        if (distance < THRESHOLD && (!bestMatch || distance < bestMatch.distance)) {
          bestMatch = {
            polyIndex: pi,
            edgeIndex: i,
            projection: projection as [number, number],
            distance,
          };
        }
      }
    }

    if (!bestMatch) return false;
    pushUndoSnapshot();

    // Insert new point after edgeIndex
    setEditingPolygons(prev => prev.map((poly, pi) => {
      if (pi !== bestMatch!.polyIndex) return poly;
      const newPoints = [...poly.points];
      newPoints.splice(bestMatch!.edgeIndex + 1, 0, bestMatch!.projection);
      return { ...poly, points: newPoints };
    }));

    // Update selected points: shift indices that are after the insertion point
    setSelectedPoints(prev => prev.map(sp => {
      const poly = editingPolygons[bestMatch!.polyIndex];
      if (sp.polygonId !== poly.id || sp.type !== poly.type) return sp;
      if (sp.idx > bestMatch!.edgeIndex) {
        return { ...sp, idx: sp.idx + 1 };
      }
      return sp;
    }));

    return true;
  }, [editingPolygons]);

  const insertPointAtEdge = useCallback((
    polygonId: string,
    type: EditingType,
    edgeIndex: number,
    normalizedPos: [number, number]
  ) => {
    pushUndoSnapshot();
    setEditingPolygons(prev => prev.map(poly => {
      if (poly.id !== polygonId || poly.type !== type) return poly;
      const next = [...poly.points];
      next.splice(edgeIndex + 1, 0, [clamp01(normalizedPos[0]), clamp01(normalizedPos[1])]);
      return { ...poly, points: next };
    }));
    setSelectedPoints(prev => prev.map(sp => {
      if (sp.polygonId !== polygonId || sp.type !== type) return sp;
      if (sp.idx > edgeIndex) return { ...sp, idx: sp.idx + 1 };
      return sp;
    }));
  }, [pushUndoSnapshot]);

  const isDirty = useMemo(() => {
    if (editingPolygons.length === 0) return false;
    return editingPolygons.some(poly => {
      const key = `${poly.type}:${poly.id}`;
      const orig = originalPointsRef.current.get(key);
      if (!orig) return true;
      if (orig.length !== poly.points.length) return true;
      for (let i = 0; i < orig.length; i++) {
        if (orig[i][0] !== poly.points[i][0] || orig[i][1] !== poly.points[i][1]) return true;
      }
      return false;
    });
  }, [editingPolygons]);

  const undo = useCallback(() => {
    const stack = undoStackRef.current;
    if (stack.length === 0) return;
    const prev = stack[stack.length - 1];
    undoStackRef.current = stack.slice(0, -1);
    setEditingPolygons(prev);
    setSelectedPoints([]);
  }, []);

  // Delete selected points from editing polygons (min 3 points must remain)
  const deleteSelectedPoints = useCallback(() => {
    if (selectedPoints.length === 0) return;
    pushUndoSnapshot();

    // Group selected indices by polygon
    const selByPoly = new Map<string, Set<number>>();
    for (const sp of selectedPoints) {
      const key = `${sp.type}:${sp.polygonId}`;
      if (!selByPoly.has(key)) selByPoly.set(key, new Set());
      selByPoly.get(key)!.add(sp.idx);
    }

    setEditingPolygons(prev => prev.map(poly => {
      const key = `${poly.type}:${poly.id}`;
      const toDelete = selByPoly.get(key);
      if (!toDelete || toDelete.size === 0) return poly;
      const remaining = poly.points.filter((_, idx) => !toDelete.has(idx));
      // Must keep at least 3 points for a valid polygon
      if (remaining.length < 3) return poly;
      return { ...poly, points: remaining };
    }));

    setSelectedPoints([]);
  }, [selectedPoints]);

  return {
    editingPolygons,
    activeEditingPolygonId,
    activeEditingType,
    selectedPoints,
    saving,
    isDirty,
    startEditing,
    handlePointSelect,
    handleSelectPointsInRect,
    clearPointSelection,
    handlePointDragStart,
    handlePointDrag,
    handlePolygonDragStart,
    handlePolygonDragMove,
    handlePolygonDrag,
    handlePointDragEnd,
    handleSaveEdit,
    handleDeletePolygon,
    cancelEditing,
    handleEdgeClick,
    insertPointAtEdge,
    deleteSelectedPoints,
    undo,
    canUndo: undoStackRef.current.length > 0,
  };
};
